import React, { Component } from "react";
import axios from "axios";
import styled from "styled-components";
import { hexToRgba } from "src/utils/colors";
import { API_URL } from "src/scenes/App";
import Initial from "./forms/Initial";
import UploadLogo from "./forms/UploadLogo";
import UploadIcon from "./forms/UploadIcon";
import StartScreen from "./forms/StartScreen";
import SelectColor from "./forms/SelectColor";
import Email from "./forms/Email";
import Finish from "./forms/Finish";
import { CustomiseContext } from "./CustomiseContext";
import { UserContext } from "src/scenes/App/UserContext";
import { ICON_STATUS } from "./forms/Finish";
import ImgMeAppGetTheApp from "src/img/ImgMeAppGetTheApp.jpg";
import { getDefaultStartScreen, getAvailableScreens } from "./components/ScreenUtils";
import {
    LottieLoadingIndicator, Toast, FlexContainer,
    BusinessPortalHeader,
    PortalCaption,
    PortalKicker,
    PortalPageShell,
    PortalSectionTitle,
    PortalSurface,
    PortalWorkspace
} from "src/components";

import { UserKeys } from "src/constants/userDetails";
import { appFeatureLevels } from "src/constants/appFeatureLevels";
import { updateFeatureLevel } from "src/utils/appFeatures";

export const Forms = {
    INITIAL: 0,
    UPLOAD: 1,
    COLOR: 2,
    ICON: 3,
    START_SCREEN: 4,
    FINISH: 5,
    EMAIL: 6,
};

export const Screens = {
    SPLASH: 0,
    MAIN: 1, //Quote
    ARTICLES: 2,
    BODY_AND_MIND: 3,
    NOTICEBOARD: 4,
    GOALS: 5,
    PLAYLISTS: 6,
    SURVEY: 7,
    PHONE: 8
};

//default values inside json files need to be updated
export const AnimationColors = {
    BACKGROUND: "#90B5FA",
    PRIMARY: "#2C349B",
    SECONDARY: "#346E73"
};

const RightColumnPhoto = styled.div`
    flex: 50%;
    border-radius: 0px 12px 12px 0px;
    overflow: hidden;
`;

const RightColumnPhotoCover = styled.div`
    background-image: url(${ImgMeAppGetTheApp});
    background-position: 50%;
    background-size: cover;
    background-repeat: no-repeat;
    width: 100%;
    height: 100%;
`;

const CustomiseEditorSurface = styled(PortalSurface)`
    min-height: 640px;
    display: grid;
    align-items: stretch;
    padding: 1.25rem;

    @media (max-width: 760px) {
        padding: 1rem;
        min-height: 0;
    }
`;

const CustomiseSteps = styled.div`
    display: grid;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const CustomiseStep = styled.div`
    display: grid;
    grid-template-columns: 1.85rem minmax(0, 1fr);
    gap: 0.65rem;
    align-items: center;
    padding: 0.65rem;
    color: ${p => p.$active ? "var(--calendar-text, #171A22)" : "var(--calendar-text-muted, #6A7387)"};
    background: ${p => p.$active ? "var(--calendar-accent-soft, rgba(91,110,245,0.12))" : "transparent"};
    border: 1px solid ${p => p.$active ? "var(--calendar-border-strong, #C9D2E7)" : "transparent"};
    border-radius: 8px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.82rem;
    font-weight: ${p => p.$active ? 800 : 700};
`;

const CustomiseStepNumber = styled.span`
    width: 1.85rem;
    height: 1.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${p => p.$active ? "var(--calendar-bg, #FFFFFF)" : "var(--calendar-text-muted, #6A7387)"};
    background: ${p => p.$active ? "var(--calendar-text, #171A22)" : "var(--calendar-surface-secondary, #F3F5FB)"};
    border: 1px solid ${p => p.$active ? "var(--calendar-text, #171A22)" : "var(--calendar-border, #D8DEEC)"};
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 800;
`;

const CustomiseEmailShell = styled(FlexContainer)`
    max-width: none;
    min-height: 640px;
    background: #FFFFFF;
    border: 1px solid #D8DEEC;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: row;

    @media (max-width: 980px) {
        flex-direction: column;
    }
`;

class Customise extends Component {
    static contextType = UserContext;
    state = {
        loading: false,
        currentForm: Forms.INITIAL,
        isNextClickable: true,
        logoFile: [],
        iconFile: [],
        icons: [],
        iconStatus: undefined,
        assetLogoUrl: "",
        tempLogoUrl: "",
        logoCacheKey: Date.now(),
        assetIconUrl: "",
        backGroundColor: AnimationColors.BACKGROUND,
        currentScreen: Screens.MAIN,
        primaryColor: AnimationColors.PRIMARY,
        secondaryColor: AnimationColors.SECONDARY,
        backgroundColorSelected: this.context[UserKeys.IS_CUSTOMIZE_COMPLETE],
        primaryColorSelected: this.context[UserKeys.IS_CUSTOMIZE_COMPLETE],
        secondaryColorSelected: this.context[UserKeys.IS_CUSTOMIZE_COMPLETE],
        errorMessage: "",
        companyName: this.context[UserKeys.COMPANY_NAME],
        employeeStartScreen: null,
        clientStartScreen: null,
        // employeeStartScreen: AppScreens.QUOTES, // Default value for new companies, overridden when loading existing settings
        // clientStartScreen: AppScreens.QUOTES, // As above
        isFinishing: undefined
    };

    getDisplayableIconUrl = (icon) => icon?.iconUrl || icon?.preview || "";
    getDisplayableIconsFromSettings = (icons, iconUrl, iconStatus) => {
        const displayIcons = icons.map(icon => ({
            uuid: icon.uuid,
            iconUrl: icon.iconUrl,
            preview: icon.iconUrl,
            selected: icon.selected,
            status: icon.status,
        }));

        if (displayIcons.length > 0 || !iconUrl) {
            return displayIcons;
        }

        return [{
            uuid: undefined,
            iconUrl: iconUrl,
            preview: iconUrl,
            selected: true,
            status: iconStatus || ICON_STATUS.PENDING,
        }];
    };
    getStoredLogoUrl = (logoUrl) => {
        if (!logoUrl) {
            return "";
        }

        if (logoUrl.startsWith("blob:")) {
            return logoUrl;
        }

        return logoUrl.split("?")[0];
    };
    getDisplayableLogoUrl = (logoUrl, cacheKey) => {
        const storedLogoUrl = this.getStoredLogoUrl(logoUrl);
        if (!storedLogoUrl || storedLogoUrl.startsWith("blob:")) {
            return storedLogoUrl;
        }

        return `${storedLogoUrl}?t=${cacheKey}`;
    };
    getStoredIconUrl = (icon) => {
        const iconUrl = icon?.iconUrl || "";
        return iconUrl && !iconUrl.startsWith("blob:") ? iconUrl : "";
    };

    componentDidMount() {
        if (this.context[UserKeys.IS_CUSTOMIZE_COMPLETE]) {
            this.setState({
                isNextClickable: true,
                currentForm: Forms.FINISH,
                loading: true
            });

            axios.get(`${API_URL}/company/settings`, {
                headers: {
                    Authorization: "Bearer " + this.context.token
                }
            }).then(res => {
                const {
                    logoUrl,
                    iconUrl,
                    iconStatus,
                    backgroundColor,
                    primaryColor,
                    secondaryColor,
                    companyName,
                    employeeStartScreen,
                    clientStartScreen,
                    icons
                } = res.data;
                const displayIcons = this.getDisplayableIconsFromSettings(icons, iconUrl, iconStatus);

                this.setState({
                    assetLogoUrl: logoUrl,
                    logoCacheKey: Date.now(),
                    assetIconUrl: iconUrl,
                    iconStatus: iconStatus,
                    icons: displayIcons,
                    isNextClickable: true,
                    currentForm: Forms.FINISH,
                    backGroundColor: backgroundColor,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    companyName: companyName,
                    employeeStartScreen: employeeStartScreen,
                    clientStartScreen: clientStartScreen,
                    loading: false
                });
            }).catch(error => {
                return Toast.error(error.getApiErrorDetails().message);
            });
        }
    }

    componentDidUpdate() {
        const featureFlags = this.context[UserKeys.FEATURE_FLAGS];
        if (!featureFlags) return;

        const availableScreens = getAvailableScreens(featureFlags);
        const defaultScreen = getDefaultStartScreen(featureFlags);

        if (!defaultScreen) return;

        if (
            !availableScreens.includes(this.state.employeeStartScreen) ||
        !availableScreens.includes(this.state.clientStartScreen)
        ) {
            this.setState({
                employeeStartScreen: defaultScreen,
                clientStartScreen: defaultScreen
            });
        }
    }

    nextLogoUploading = () => {
        this.setState({
            assetLogoUrl: "",
            isNextClickable: false,
            tempLogoUrl: ""
        });
    };

    nextLogoAvailable = (tempLogoUrl) => {
        this.context.appFns.updateUserContextProperties({
            [UserKeys.COMPANY_LOGO_URL]: tempLogoUrl,
        });

        this.setState({
            isNextClickable: true,
            tempLogoUrl: tempLogoUrl
        });
    };

    updatePendingIconUrl = (previewUrl, iconUrl) => {
        this.setState(({ iconFile }) => ({
            iconFile: iconFile.map(icon =>
                icon.preview === previewUrl
                    ? { ...icon, iconUrl }
                    : icon
            ),
            assetIconUrl: iconUrl
        }));
    };

    setCurrentScreen = index => {
        const { currentForm } = this.state;
        const screenIds = this.getSlidesForScreen(currentForm);
        this.setState({
            currentScreen: (index + screenIds.length) % screenIds.length
        });
    };

    leftScreen = () => {
        const { currentScreen } = this.state;
        this.setCurrentScreen(currentScreen - 1);
    };

    rightScreen = () => {
        const { currentScreen } = this.state;
        this.setCurrentScreen(currentScreen + 1);
    };

    getMergedIcons = () => {
        return this.getMergedIconsFrom(this.state.iconFile, this.state.icons);
    };

    getMergedIconsFrom = (iconFile, icons) => {
        const mergedIconPayload = [
            ...iconFile.map(icon => ({
                uuid: icon.uuid,
                iconUrl: icon.iconUrl ?? null,
                preview: icon.preview || icon.iconUrl || "",
                fileToUpload: icon.fileToUpload,
                selected: icon.selected ?? false,
                status: icon.status ?? ICON_STATUS.PENDING,
                source: icon.source || "file",
            })),
            ...icons.map(icon => ({
                uuid: icon.uuid,
                iconUrl: icon.iconUrl,
                preview: icon.preview || icon.iconUrl || "",
                selected: icon.selected ?? false,
                status: icon.status ?? ICON_STATUS.PENDING,
                source: icon.source || "backend",
            }))
        ];

        const selectedIcon =
        mergedIconPayload.find(icon => icon.selected) || mergedIconPayload[0];

        return { mergedIconPayload, selectedIcon };
    };

    uploadLogoFile = async (logo) => {
        const fileToUpload = logo.fileToUpload;
        const fileExtension = fileToUpload.name.split(".").pop();
        const presigned = await axios.get(`${API_URL}/company/generateUrl/${fileExtension}`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        });
        const fullUrl = presigned.data.url;

        await axios.put(fullUrl, fileToUpload, {
            headers: {
                "Content-Type": fileToUpload.type
            }
        });

        return fullUrl.split("?")[0];
    };

    uploadIconFile = async (icon) => {
        if (this.getStoredIconUrl(icon)) {
            return icon;
        }

        const fileToUpload = icon.fileToUpload;
        if (!fileToUpload) {
            return icon;
        }

        const fileExtension = fileToUpload.name.split(".").pop().toLowerCase();
        const presigned = await axios.get(`${API_URL}/company/generateAppIconUrl/${fileExtension}`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        });
        const fullUrl = presigned.data.url;
        const storedUrl = fullUrl.split("?")[0];

        await axios.put(fullUrl, fileToUpload, {
            headers: {
                "Content-Type": fileToUpload.type
            }
        });

        return {
            ...icon,
            iconUrl: storedUrl,
            preview: icon.preview || storedUrl
        };
    };

    uploadIconFiles = async (iconFile) => {
        const uploadedIcons = [];
        for (const icon of iconFile) {
            uploadedIcons.push(await this.uploadIconFile(icon));
        }

        return uploadedIcons;
    };

    nextClick = async () => {
        const {
            logoFile,
            tempLogoUrl,
            assetLogoUrl,
            backGroundColor,
            primaryColor,
            secondaryColor,
            employeeStartScreen,
            clientStartScreen
        } = this.state;

        if (!this.state.isNextClickable) {
            return;
        }

        if (this.state.currentForm === Forms.INITIAL) {
            this.setState({
                currentForm: Forms.UPLOAD,
                isNextClickable: tempLogoUrl !== "",
            });
        } else if (this.state.currentForm === Forms.UPLOAD) {
            this.setState({
                currentScreen: Screens.MAIN,
                currentForm: Forms.COLOR
            });
        } else if (this.state.currentForm === Forms.COLOR) {
            this.setState({ currentForm: Forms.ICON });
        } else if (this.state.currentForm === Forms.ICON) {
            this.setState({ currentForm: Forms.START_SCREEN });
        } else if (this.state.currentForm === Forms.START_SCREEN) {
            this.setState({ isFinishing: true });

            const isCustomizeComplete = this.context[UserKeys.IS_CUSTOMIZE_COMPLETE];
            try {
                const logoUrl = tempLogoUrl === ""
                    ? this.getStoredLogoUrl(assetLogoUrl)
                    : await this.uploadLogoFile(logoFile[0]);
                const logoCacheKey = Date.now();
                const displayLogoUrl = this.getDisplayableLogoUrl(logoUrl, logoCacheKey);
                const uploadedIconFile = await this.uploadIconFiles(this.state.iconFile);
                const { mergedIconPayload, selectedIcon } = this.getMergedIconsFrom(
                    uploadedIconFile,
                    this.state.icons
                );
                const hasIconsToSave = mergedIconPayload.length > 0;

                const selectedIconUrl = selectedIcon
                    ? this.getStoredIconUrl(selectedIcon)
                    : "";

                const storedIconPayload = mergedIconPayload
                    .filter(icon => this.getStoredIconUrl(icon))
                    .map(icon => ({
                        uuid: icon.uuid,
                        iconUrl: icon.iconUrl,
                        selected: icon.selected ?? false,
                        status: icon.status
                    }));
                const displayIcons = mergedIconPayload.map(icon => ({
                    uuid: icon.uuid,
                    iconUrl: icon.iconUrl,
                    preview: icon.preview || icon.iconUrl || "",
                    selected: icon.selected ?? false,
                    status: icon.status ?? ICON_STATUS.PENDING,
                    source: "backend",
                }));

                const payload = {
                    logoUrl,
                    iconUrl: hasIconsToSave ? selectedIconUrl : null,
                    iconUrls: storedIconPayload,
                    backgroundColor: backGroundColor,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    employeeStartScreen: employeeStartScreen,
                    clientStartScreen: clientStartScreen
                };

                const res = isCustomizeComplete
                    ? await axios.put(`${API_URL}/company/settings`, payload, {
                        headers: { Authorization: `Bearer ${this.context.token}` },
                    })
                    : await axios.post(`${API_URL}/company/settings`, payload, {
                        headers: { Authorization: `Bearer ${this.context.token}` },
                    });

                //Header - Company Branding
                this.context.appFns.updateUserContextProperties({
                    [UserKeys.COMPANY_BACKGROUND_COLOR]: res.data.backgroundColor,
                    [UserKeys.COMPANY_PRIMARY_COLOR]: res.data.primaryColor,
                    [UserKeys.COMPANY_LOGO_URL]: displayLogoUrl,
                    [UserKeys.COMPANY_LOGO_UPDATED_AT]: logoCacheKey,
                });

                if (!isCustomizeComplete) {
                    this.context.appFns.updateUserContextProperties({
                        [UserKeys.PERMISSIONS]: JSON.parse(res.data.permissions),
                    });
                    this.context.appFns.updateUserContextProperties({
                        [UserKeys.IS_CUSTOMIZE_COMPLETE]: true,
                    });
                    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const featureLevel = appFeatureLevels.FEATURE_LEVEL_3; // used to set what feature level we're currently at
                    updateFeatureLevel(featureLevel, timeZone, this.context.token);
                }

                this.setState({
                    currentForm: Forms.FINISH,
                    isFinishing: false,
                    currentScreen: Screens.MAIN,
                    iconStatus: res.data.iconStatus,
                    icons: displayIcons,
                    iconFile: [],
                    assetLogoUrl: logoUrl,
                    logoCacheKey: logoCacheKey,
                    assetIconUrl: selectedIconUrl,
                    tempLogoUrl: "",
                    backGroundColor: backGroundColor,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    employeeStartScreen: employeeStartScreen,
                    clientStartScreen: clientStartScreen
                });
            } catch (error) {
                this.setState({ isFinishing: false });
                Toast.error(error.getApiErrorDetails().message);
            }
        } else if (this.state.currentForm === Forms.EMAIL) {
            this.setState({ currentForm: Forms.FINISH });
        }
    };

    backToCustomise = () => {
        this.setState({
            currentScreen: Screens.MAIN,
            currentForm: this.context.isCustomizeComplete ? Forms.FINISH : Forms.INITIAL,
            isNextClickable: true,
        });
    };

    goToStep1 = () => {
        this.setState({
            currentForm: Forms.UPLOAD, 
            currentScreen: Screens.MAIN,
        });
    };

    previousClick = (currentForm, currentScreen) => {
        this.setState({
            currentForm: currentForm, 
            currentScreen: currentScreen,
        });
    };

    handleChange = (name, value) => {
        this.setState(() => ({
            [name]: value,
        }));
    };

    setColor = (color) => {
        //detect if this color comes from color picker,  or converted hex color result when loading
        if (color.rgb) {
            return [color.rgb.r / 255, color.rgb.g / 255, color.rgb.b / 255, 1];
        } else {
            return color;
        }
    };

    updateBackgroundColor = (color) => {
        if (color.hex !== this.state.primaryColor && color.hex !== this.state.secondaryColor) {
            this.setState({
                backGroundColor: color.hex
            });
        }
    };

    updatePrimaryColor = (color) => {
        if (color.hex !== this.state.backGroundColor && color.hex !== this.state.secondaryColor) {
            this.setState({
                primaryColor: color.hex
            });
        }
    };

    updateSecondaryColor = (color) => {
        if (color.hex !== this.state.backGroundColor && color.hex !== this.state.primaryColor) {
            this.setState({
                secondaryColor: color.hex
            });
        }
    };

    setBackgroundColorSelected = (selected) => {
        this.setState({ backgroundColorSelected: selected });
    };

    setPrimaryColorSelected = (selected) => {
        this.setState({ primaryColorSelected: selected });
    };

    setSecondaryColorSelected = (selected) => {
        this.setState({ secondaryColorSelected: selected });
    };

    /**
     * @param {int} currentForm
     * @return {int[]}
     */
    getSlidesForScreen(currentForm) {
        switch (currentForm) {
        //case Forms.UPLOAD:
        //    return [ Screens.SPLASH, Screens.MAIN ];
        default:
            return [ Screens.SPLASH, Screens.MAIN, Screens.ARTICLES, Screens.BODY_AND_MIND, Screens.NOTICEBOARD, Screens.GOALS, Screens.PLAYLISTS, Screens.SURVEY];
        }
    }

    /**
     * Reads colours from state. If any one (or more) is still in hex format #RRGGBB it will be
     * converted to an RGBA arrays of floats.
     * @returns {{backgroundColor: float[], primaryColor: float[], secondaryColor: float[]}}
     */
    buildColourPalette = () => {
        let { primaryColor, secondaryColor, backGroundColor } = this.state;
        primaryColor = hexToRgba(primaryColor);
        secondaryColor = hexToRgba(secondaryColor);
        backGroundColor = hexToRgba(backGroundColor);
        return {
            primaryColor: primaryColor,
            secondaryColor: secondaryColor,
            backgroundColor: backGroundColor
        };
    };

    render() {
        const {
            loading,
            currentForm,
            logoFile,
            iconFile,
            currentScreen,
            isNextClickable,
            backGroundColor,
            primaryColor,
            secondaryColor,
            backgroundColorSelected,
            primaryColorSelected,
            secondaryColorSelected,
            assetLogoUrl,
            tempLogoUrl,
            logoCacheKey,
            assetIconUrl,
            iconStatus,
            companyName,
            employeeStartScreen,
            clientStartScreen,
            isFinishing
        } = this.state;

        const screenIds = this.getSlidesForScreen(currentForm);
        const { selectedIcon } = this.getMergedIcons();
        const displayAssetLogoUrl = this.getDisplayableLogoUrl(assetLogoUrl, logoCacheKey);
        const currentLogoUrl = tempLogoUrl || displayAssetLogoUrl;
        const stepLabels = [
            { id: Forms.INITIAL, label: "Start" },
            { id: Forms.UPLOAD, label: "Logo" },
            { id: Forms.COLOR, label: "Brand Colours" },
            { id: Forms.ICON, label: "App Icon" },
            { id: Forms.START_SCREEN, label: "Start Screen" },
            { id: Forms.FINISH, label: "Overview" },
        ];

        return (
            <PortalPageShell maxWidth="98.625rem">
                <BusinessPortalHeader
                    title="My Me App"
                    description="Shape your branded mobile experience with logos, colours, icons, and the first screen your users see."
                />

                <CustomiseContext.Provider value={{
                    nextLogoAvailable: this.nextLogoAvailable,
                    nextLogoUploading: this.nextLogoUploading,
                    updatePendingIconUrl: this.updatePendingIconUrl,
                    nextClick: this.nextClick,
                    logoFile: logoFile,
                    iconFile: iconFile,
                    assetLogoUrl: displayAssetLogoUrl,
                    tempLogoUrl: tempLogoUrl,
                    currentLogoUrl: currentLogoUrl,
                    assetIconUrl: this.getDisplayableIconUrl(selectedIcon) || assetIconUrl,
                    iconStatus: iconStatus,
                    currentForm: currentForm,
                    screenIds: screenIds,
                    token: this.context.token,
                    setCurrentScreen: this.setCurrentScreen,
                    leftScreen: this.leftScreen,
                    rightScreen: this.rightScreen,
                    currentScreen: currentScreen,
                    uploadFile: this.handleChange,
                    isNextClickable: isNextClickable,
                    backToCustomise: this.backToCustomise,
                    previousClick: this.previousClick,
                    backGroundColor: backGroundColor,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    backgroundColorSelected: backgroundColorSelected,
                    primaryColorSelected: primaryColorSelected,
                    secondaryColorSelected: secondaryColorSelected,
                    colorChange: this.handleChange,
                    goToStep1: this.goToStep1,
                    companyName: companyName,
                    colourPalette: this.buildColourPalette(),
                    updateBackgroundColor: this.updateBackgroundColor,
                    updatePrimaryColor: this.updatePrimaryColor,
                    updateSecondaryColor: this.updateSecondaryColor,
                    setBackgroundColorSelected: this.setBackgroundColorSelected,
                    setPrimaryColorSelected: this.setPrimaryColorSelected,
                    setSecondaryColorSelected: this.setSecondaryColorSelected,
                    updateUserStartScreen: this.handleChange,
                    employeeStartScreen: employeeStartScreen,
                    clientStartScreen: clientStartScreen,
                    isComplete: this.context[UserKeys.IS_CUSTOMIZE_COMPLETE],
                    email: this.context.email,
                    appVariant: this.context[UserKeys.APP_VARIANT],
                    isFinishing: isFinishing,
                    icons: this.state.icons,
                    featureFlags: this.context[UserKeys.FEATURE_FLAGS],
                }}>
                    { currentForm === Forms.EMAIL &&
                        <CustomiseEditorSurface padding="1.25rem">
                            <CustomiseEmailShell>
                                <div style={{ padding: "clamp(1.25rem, 3vw, 2.5rem)", flex: "50%" }}>
                                    <Email />
                                </div>

                                <RightColumnPhoto>
                                    <RightColumnPhotoCover />
                                </RightColumnPhoto>
                            </CustomiseEmailShell>
                        </CustomiseEditorSurface>
                    }

                    { currentForm !== Forms.EMAIL &&
                        <PortalWorkspace columns="18rem minmax(0, 1fr)" breakpoint="980px">
                            <PortalSurface padding="1rem">
                                <PortalKicker>Setup Flow</PortalKicker>
                                <PortalSectionTitle>Branding Steps</PortalSectionTitle>
                                <PortalCaption>
                                    Move through each stage to keep the mobile app aligned with your organisation.
                                </PortalCaption>
                                <CustomiseSteps>
                                    {stepLabels.map((step, index) => (
                                        <CustomiseStep key={step.id} $active={currentForm === step.id}>
                                            <CustomiseStepNumber $active={currentForm === step.id}>{index + 1}</CustomiseStepNumber>
                                            <span>{step.label}</span>
                                        </CustomiseStep>
                                    ))}
                                </CustomiseSteps>
                            </PortalSurface>
                            <CustomiseEditorSurface>
                                <LoadingContainer loading={loading}>
                                    { currentForm === Forms.INITIAL &&
                                        <Initial /> }
                                    { currentForm === Forms.UPLOAD &&
                                        <UploadLogo /> }
                                    { currentForm === Forms.COLOR &&
                                        <SelectColor /> }
                                    { currentForm === Forms.ICON &&
                                        <UploadIcon /> }
                                    { currentForm === Forms.START_SCREEN &&
                                        <StartScreen /> }
                                    { currentForm === Forms.FINISH &&
                                        <Finish /> }
                                </LoadingContainer>
                            </CustomiseEditorSurface>
                        </PortalWorkspace>
                    }
                </CustomiseContext.Provider>
            </PortalPageShell>
        );
    }
}

const LoadingContainer = ({ loading, children }) => {
    return (
        <>
            { loading && <LottieLoadingIndicator /> }
            { !loading && children }
        </>
    );
};
 
export default Customise;
